import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, CircleCheck, Volume2, VolumeX, CheckCircle, AlertCircle, Search } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../store/slices/uiSlice';
import api from '../services/api';
import ModalPortal from './ModalPortal';
import socketService from '../services/socketService';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 32px;
  max-width: 1000px; /* Increased from 800px */
  max-height: 90vh; /* Increased from 80vh */
  width: 90vw; /* Added width for better responsiveness */
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative; /* Ensure modal content stays in place */
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  
  &:hover {
    color: #333;
  }
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const DocumentSection = styled.div`
  margin-bottom: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const DocumentHeader = styled.div`
  background: #f5f5f5;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  
  &:hover {
    background: #ececec;
  }
`;

const DocumentTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const PageCount = styled.span`
  color: #666;
  font-size: 14px;
  margin-left: 8px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.$status === 'complete' ? '#10b981' : '#f59e0b'};
  color: white;
  cursor: help;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const ChunkList = styled.div`
  padding: 16px;
  display: ${props => props.$expanded ? 'block' : 'none'};
`;

const ChunkItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
`;

const ChunkInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChunkLabel = styled.span`
  color: #333;
  font-weight: 500;
`;

const ProcessButton = styled.button`
  background: #d1d5db; /* Gray 300 */
  color: #374151; /* Dark gray text */
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${props => props.$processing || props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  min-width: 100px;
  
  &:hover {
    background: ${props => props.$processing ? '#d1d5db' : '#9ca3af'}; /* Gray 400 on hover */
  }
  
  &:disabled {
    opacity: 0.6;
  }
`;

const Checkmark = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #10b981; /* Success green */
  font-size: 16px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap; /* Prevent text wrapping on hover */
  
  &:hover {
    background: #d1fae5;
    color: #059669;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProgressText = styled.div`
  text-align: center;
  margin: 16px 0;
  padding: 16px;
  background: #e3f2fd;
  border-radius: 4px;
  color: #1976d2;
  font-weight: 500;
  min-height: 52px; /* Reserve space to prevent layout shifts */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChunkProgressBar = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress || 0}%;
`;

const ProgressStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
`;

const ProgressMessage = styled.span`
  font-weight: 500;
  color: #1e293b;
`;

const MuteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: ${props => props.ignored ? '#fef3c7' : '#f9fafb'};
  color: ${props => props.ignored ? '#d97706' : '#6b7280'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.ignored ? '#fde68a' : '#f3f4f6'};
    border-color: ${props => props.ignored ? '#f59e0b' : '#9ca3af'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background: #2563eb;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ChunkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ChunkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;



const PrimaryButton = styled.button`
  background: #3b82f6; /* Primary blue */
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500; /* Reduced from 600 to 500 */
  font-size: 16px;
  
  &:hover {
    background: ${props => props.disabled ? '#3b82f6' : '#2563eb'}; /* Darker blue on hover */
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #6b7280; /* Gray text */
  border: 1px solid #d1d5db; /* Gray 300 outline */
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500; /* Reduced from 600 to 500 */
  font-size: 16px;
  
  &:hover {
    background: #f9fafb; /* Very light gray background on hover */
    border-color: #9ca3af; /* Gray 400 outline on hover */
  }
`;

const ChunkingModal = ({ 
  largeFiles, 
  onComplete, 
  onCancel,
  loanId 
}) => {
  const dispatch = useDispatch();
  
  // Function to determine document type from filename
  const determineDocumentType = (filename) => {
    const filenameLower = filename.toLowerCase();
    
    // Define patterns for document type detection (matching backend logic)
    if (filenameLower.includes('note') || filenameLower.includes('promissory')) {
      return 'note';
    } else if (filenameLower.includes('proposal') || filenameLower.includes('credit memo') || filenameLower.includes('loan request')) {
      return 'proposal';
    } else if (filenameLower.includes('service agreement') || filenameLower.includes('security agreement') || filenameLower.includes('collateral')) {
      return 'service_agreement';
    } else if (filenameLower.includes('checklist') || filenameLower.includes('check list')) {
      return 'checklist';
    } else if (filenameLower.includes('archive') || filenameLower.includes('ppp') || filenameLower.includes('historical')) {
      return 'archive';
    } else {
      return 'additional'; // Default catch-all
    }
  };

  const getDocumentStatus = (file) => {
    const isRegularFile = !file.chunks || file.chunks.length === 0;
    
    if (isRegularFile) {
      // For regular files, check if processed OR ignored
      const isProcessed = processedRegularFiles[file.filename];
      const isIgnored = isFileIgnored(file);
      const status = (isProcessed || isIgnored) ? 'complete' : 'needs_attention';
      
      console.log(`🔍 [DOCUMENT STATUS] File: ${file.filename}, isProcessed: ${isProcessed}, isIgnored: ${isIgnored}, status: ${status}`);
      console.log(`🔍 [DOCUMENT STATUS] processedRegularFiles:`, processedRegularFiles);
      
      return status;
    } else {
      // For chunked files, check if all chunks are processed or ignored
      const totalChunks = file.chunks ? file.chunks.length : 0;
      let processedCount = 0;
      let ignoredCount = 0;
      
      if (file.chunks) {
        file.chunks.forEach(chunk => {
          const chunkKey = `${file.filename}_${chunk.chunk_index}`;
          const isProcessed = processedChunks[chunkKey];
          const isIgnored = isChunkIgnored(file, chunk);
          
          // console.log(`🔍 [DOCUMENT STATUS DEBUG] Chunk ${chunk.chunk_index}:`, {
          //   chunkKey,
          //   isProcessed,
          //   isIgnored,
          //   processedChunks: Object.keys(processedChunks),
          //   ignoredChunks: Object.keys(ignoredChunks),
          //   localIgnoredChunks: Object.keys(localIgnoredChunks)
          // });
          
          // Special debugging for chunk 0
          // if (chunk.chunk_index === 0) {
          //   console.log(`🔍 [CHUNK 0 DEBUG] Special debugging for chunk 0:`);
          //   console.log(`🔍 [CHUNK 0 DEBUG] file.filename: ${file.filename}`);
          //   console.log(`🔍 [CHUNK 0 DEBUG] chunk.chunk_index: ${chunk.chunk_index}`);
          //   console.log(`🔍 [CHUNK 0 DEBUG] chunkKey: ${chunkKey}`);
          //   console.log(`🔍 [CHUNK 0 DEBUG] processedChunks[${chunkKey}]: ${processedChunks[chunkKey]}`);
          //   console.log(`🔍 [CHUNK 0 DEBUG] All processedChunks keys:`, Object.keys(processedChunks));
          //   console.log(`🔍 [CHUNK 0 DEBUG] Keys containing filename:`, Object.keys(processedChunks).filter(key => key.includes(file.filename)));
          // }
          
          // Count processed and ignored chunks
          if (isProcessed) {
            processedCount++;
          }
          if (isIgnored) {
            ignoredCount++;
          }
        });
      }
      
      // console.log(`🔍 [DOCUMENT STATUS DEBUG] ${file.filename}:`, {
      //   totalChunks,
      //   processedCount,
      //   ignoredCount,
      //   sum: processedCount + ignoredCount,
      //   status: processedCount + ignoredCount === totalChunks && totalChunks > 0 ? 'complete' : 'needs_attention'
      // });
      
      // Complete if all chunks are either processed or ignored
      if (processedCount + ignoredCount === totalChunks && totalChunks > 0) {
        return 'complete';
      } else {
        return 'needs_attention';
      }
    }
  };
  
  const [expandedDocs, setExpandedDocs] = useState({});
  const [processedChunks, setProcessedChunks] = useState({});
  const [processingChunks, setProcessingChunks] = useState({});
  const [files, setFiles] = useState([]);
  const [chunkProgress, setChunkProgress] = useState({});
  const [globalProgress, setGlobalProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [ignoredChunks, setIgnoredChunks] = useState({});
  const [localIgnoredChunks, setLocalIgnoredChunks] = useState({}); // Tracks locally ignored unprocessed chunks
  const [ignoredFiles, setIgnoredFiles] = useState({}); // Tracks ignored state for regular files
  const [isProcessingRegularFile, setIsProcessingRegularFile] = useState({}); // Tracks processing state for each regular file
  const [processedRegularFiles, setProcessedRegularFiles] = useState({}); // Tracks completion state for each regular file
  const [chunkData, setChunkData] = useState({}); // Store full chunk data with IDs
  const [messageCount, setMessageCount] = useState(0); // Track total messages received
  const [isInitializing, setIsInitializing] = useState(true); // Show processing spinner on initial load
  
  // Auto-hide progress bar when it reaches 100%
  useEffect(() => {
    if (globalProgress === 100 && progressMessage) {
      console.log('🎉 [ChunkingModal] Progress reached 100%, auto-hiding in 2 seconds...');
      const timeout = setTimeout(() => {
        setProgressMessage('');
        setGlobalProgress(0);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [globalProgress, progressMessage]);
  

  useEffect(() => {
    // Initialize state from largeFiles prop
    if (largeFiles && largeFiles.length > 0) {
      console.log('🔄 [CHUNKING MODAL] Initializing files:', largeFiles.map(f => ({
        filename: f.filename,
        isExisting: f.isExisting,
        file_id: f.file_id,
        initialIgnoredState: f.initialIgnoredState
      })));
      
      setFiles(largeFiles);
      
      // Initialize ignored state for existing files
      const initialIgnoredStates = {};
      largeFiles.forEach(file => {
        if (file.isExisting && file.initialIgnoredState !== undefined) {
          initialIgnoredStates[file.filename] = file.initialIgnoredState;
          console.log(`🔄 [CHUNKING MODAL] Setting initial ignored state for ${file.filename}: ${file.initialIgnoredState}`);
        }
      });
      
      if (Object.keys(initialIgnoredStates).length > 0) {
        setIgnoredFiles(prev => ({ ...prev, ...initialIgnoredStates }));
        console.log('🔄 [CHUNKING MODAL] Initialized ignored states:', initialIgnoredStates);
      }
      
      // Expand first document by default
      const firstDoc = largeFiles[0];
      if (firstDoc) {
        setExpandedDocs({ [firstDoc.filename]: true });
      }
    }
  }, [largeFiles]);

  useEffect(() => {
    // Check for already processed chunks when files change
    if (files && files.length > 0) {
      const initializeModal = async () => {
        await checkProcessedChunks();
        await loadIgnoredState();
        setIsInitializing(false); // Hide processing spinner after initialization
      };
      initializeModal();
    } else if (files !== null && files.length === 0) {
      // No files - hide spinner immediately
      setIsInitializing(false);
    }
  }, [files]);

  // Create stable callback for progress updates using useCallback
  // NOTE: Removed messageCount, progressMessage, and processingChunks from dependencies
  // to prevent callback recreation on every message, which causes unsubscribe/resubscribe
  // and can lead to missed messages. Use functional state updates instead.
  const handleProgressUpdate = useCallback((message) => {
    setMessageCount(prev => prev + 1);
    
    try {
      const data = message;

      // Handle both chunk and main progress updates
      if ((data.scope === 'chunk' || data.scope === 'main') && data.payload) {
        const { progress, status } = data.payload;
        
        // Set the progress message from the status
        if (status) {
          setProgressMessage(status);
        }
        
        // Update global progress for all progress updates (including individual file processing)
        if (progress !== undefined) {
          setGlobalProgress(progress);
          
          // Update chunk progress using functional update to access current processingChunks
          setProcessingChunks(prevProcessingChunks => {
            const currentProcessing = Object.keys(prevProcessingChunks).find(key => prevProcessingChunks[key]);
            
            // Also update chunk progress if there's a current processing chunk
            if (currentProcessing) {
              setChunkProgress(prev => ({
                ...prev,
                [currentProcessing]: progress
              }));
            }
            
            return prevProcessingChunks; // Return unchanged
          });
        }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }, []); // Empty dependencies - callback never changes, preventing unsubscribe/resubscribe issues

  // Create stable callback for room joined events
  const handleRoomJoined = useCallback((data) => {
    console.log(`✅ [ChunkingModal] Room joined confirmation: ${data.room}`);
  }, []);

  useEffect(() => {
    // Connect to the centralized WebSocket service
    socketService.connect();
    
    // Subscribe to progress updates immediately (socketService handles connection state)
    // This ensures we catch messages even if they arrive during connection
    socketService.subscribe('progress_update', handleProgressUpdate);
    socketService.subscribe('room_joined', handleRoomJoined);
    
    // Ensure we're connected and joined to the chunk room
    const ensureConnection = async () => {
      try {
        await socketService.waitForConnection(5000);
        // Explicitly join the chunk room for individual file processing
        socketService.joinRoom('chunk');
      } catch (error) {
        console.warn('⚠️ [ChunkingModal] WebSocket connection timeout:', error);
      }
    };
    
    ensureConnection();

    return () => {
      socketService.unsubscribe('progress_update', handleProgressUpdate);
      socketService.unsubscribe('room_joined', handleRoomJoined);
    };
  }, [handleProgressUpdate, handleRoomJoined]); // Include the stable callbacks

  const checkProcessedChunks = async () => {
    try {
      console.log('🔍 [CHECK PROCESSED DEBUG] Starting checkProcessedChunks with single API call...');
      
      // Make ONE API call to get all chunk status for the loan
      const response = await api.get(`/chunks/check-all-chunk-status/${loanId}`);
      
      if (response.data.success) {
        // console.log('🔍 [CHECK PROCESSED DEBUG] Single API response:', response.data);
        // console.log('🔍 [CHECK PROCESSED DEBUG] processed_chunks_status:', response.data.processed_chunks_status);
        // console.log('🔍 [CHECK PROCESSED DEBUG] processed_chunks:', response.data.processed_chunks);
        // console.log('🔍 [CHECK PROCESSED DEBUG] ignored_chunks_status:', response.data.ignored_chunks_status);
        
        // Process all files from the single response
        for (const file of files) {
          const filename = file.filename || file.name;
          if (!filename) {
            console.warn('File missing filename/name:', file);
            continue;
          }
          
          // Check if it's a regular file (no chunks)
          if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
            // For regular files, check if they're processed
            const statusData = response.data.processed_chunks_status[filename];
            const isProcessed = statusData && statusData.processed;
            
            // Also check if this is an existing file (already has file_id from DB query)
            const isExistingFile = file.isExisting || file.file_id;
            
            setProcessedRegularFiles(prev => ({ ...prev, [filename]: isProcessed || isExistingFile }));
            
            console.log(`🔍 [CHECK PROCESSED DEBUG] Regular file ${filename}: isProcessed=${isProcessed}, isExisting=${isExistingFile}`);
            
            // Store the file_hash if available for database updates
            if ((isProcessed || isExistingFile) && statusData) {
              if (statusData.file_hash) {
                file.file_hash = statusData.file_hash;
              }
              if (statusData.id && !file.file_id) {
                file.file_id = statusData.id;
              }
              console.log(`🔍 [FILE HASH DEBUG] Found data for ${filename}: file_hash=${statusData.file_hash}, file_id=${statusData.id || file.file_id}`);
            } else if (isExistingFile && !statusData) {
              console.log(`🔍 [FILE HASH DEBUG] Existing file ${filename} already has file_id: ${file.file_id}`);
            } else {
              console.log(`🔍 [FILE HASH DEBUG] No file_hash found for ${filename}`);
            }
          } else {
            // Handle chunked files
            const filenamePattern = filename.replace(/\.pdf$/i, '');
            // console.log(`🔍 [CHUNKED FILE DEBUG] Processing chunked file: ${filename}`);
            // console.log(`🔍 [CHUNKED FILE DEBUG] filenamePattern: ${filenamePattern}`);
            // console.log(`🔍 [CHUNKED FILE DEBUG] Available processed_chunks:`, response.data.processed_chunks);
            
            const isProcessed = response.data.processed_chunks && response.data.processed_chunks.some(chunk => 
              chunk.filename.includes(filenamePattern)
            );
            setProcessedRegularFiles(prev => ({ ...prev, [filename]: isProcessed }));
            
            // console.log(`🔍 [CHECK PROCESSED DEBUG] Chunked file ${filename}: isProcessed=${isProcessed}`);
            
            // Store chunk data if available
            if (isProcessed && response.data.processed_chunks) {
              const processed = {};
              const chunkData = {};
              
              // First, find matching chunks by filename pattern to get the file_hash
              const filenamePattern = filename.replace(/\.pdf$/i, '');
              const matchingChunks = response.data.processed_chunks.filter(chunk => chunk.filename.includes(filenamePattern));
              // console.log(`🔍 [CHUNK MATCHING DEBUG] Found ${matchingChunks.length} matching chunks for ${filenamePattern}:`, matchingChunks);
              
              // Set the file_hash from the first matching chunk (all chunks should have the same normalized file_hash)
              if (matchingChunks.length > 0) {
                file.file_hash = matchingChunks[0].file_hash;
                // console.log(`🔍 [FILE HASH DEBUG] Set file_hash for ${filename}: ${file.file_hash}`);
              }
              
              matchingChunks.forEach(chunk => {
                const key = `${filename}_${chunk.chunk_index}`;
                // console.log(`🔍 [CHUNK PROCESSING DEBUG] Processing chunk: ${chunk.filename} -> key: ${key}`);
                // console.log(`🔍 [CHUNK PROCESSING DEBUG] chunk.chunk_index: ${chunk.chunk_index} (type: ${typeof chunk.chunk_index})`);
                // console.log(`🔍 [CHUNK PROCESSING DEBUG] chunk.filename: ${chunk.filename}`);
                // console.log(`🔍 [CHUNK PROCESSING DEBUG] chunk.file_hash: ${chunk.file_hash}`);
                processed[key] = true;
                chunkData[key] = chunk; // Store full chunk data with ID
              });
              // console.log('🔍 Loading processed chunks for', filename, ':', processed);
              // console.log('🔍 [CHUNK DATA DEBUG] Stored chunk data:', chunkData);
              setProcessedChunks(prev => ({ ...prev, ...processed }));
              setChunkData(prev => ({ ...prev, ...chunkData }));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking chunk status:', error);
    }
  };

  const loadIgnoredState = async () => {
    try {
      // console.log('🔍 [LOAD IGNORED DEBUG] Loading ignored state with single API call...');
      
      // Make ONE API call to get all ignored state for the loan
      const response = await api.get(`/chunks/check-all-chunk-status/${loanId}`);
      
      if (response.data.success) {
        // console.log('🔍 [LOAD IGNORED DEBUG] Single API response:', response.data);
        
        const ignoredFiles = {};
        const ignoredChunks = {};
        
        // Process ignored state for all files
        if (response.data.ignored_chunks_status) {
          // console.log('🔍 [LOAD IGNORED DEBUG] All ignored_chunks_status entries:', response.data.ignored_chunks_status);
          Object.entries(response.data.ignored_chunks_status).forEach(([filename, isIgnored]) => {
            // console.log(`🔍 [LOAD IGNORED DEBUG] Processing entry: ${filename} -> ignored: ${isIgnored}`);
            // Check if this is a chunk file or regular file
            if (filename.includes('chunk')) {
              // This is a chunk file
              const fileBase = filename.split('_chunk')[0]; // Extract base filename
              // Handle both _chunk_001 and _chunk_001-025 patterns
              const chunkMatch = filename.match(/_chunk_(\d+)(?:-\d+)?/);
              const startPage = chunkMatch?.[1] || '1';
              
              // Convert starting page to 0-based chunk index
              // Pages: 1->0, 23->1, 45->2, 67->3, 89->4
              const pageNum = parseInt(startPage);
              let chunkIndex = 0;
              if (pageNum === 1) chunkIndex = 0;
              else if (pageNum === 23) chunkIndex = 1;
              else if (pageNum === 45) chunkIndex = 2;
              else if (pageNum === 67) chunkIndex = 3;
              else if (pageNum === 89) chunkIndex = 4;
              else chunkIndex = pageNum - 1; // Fallback
              
              // The database filename format is: base_chunk_001.pdf
              // But the frontend filename format is: base.pdf
              // So we need to convert the database format to match the frontend format
              // by removing the _chunk_001 part and keeping the .pdf extension
              const frontendFilename = fileBase + '.pdf';
              const key = `${frontendFilename}_${chunkIndex}`;
              
              // console.log(`🔍 [CHUNK IGNORED DEBUG] Processing chunk: ${filename} -> key: ${key}, ignored: ${isIgnored}`);
              // console.log(`🔍 [CHUNK IGNORED DEBUG] fileBase: ${fileBase}, frontendFilename: ${frontendFilename}, chunkIndex: ${chunkIndex}, parsed: ${parseInt(chunkIndex)}`);
              ignoredChunks[key] = isIgnored;
            } else {
              // This is a regular file
              // console.log(`🔍 [REGULAR FILE IGNORED DEBUG] Processing regular file: ${filename} -> ignored: ${isIgnored}`);
              ignoredFiles[filename] = isIgnored;
            }
          });
        }
        
        console.log('🔍 [LOAD IGNORED DEBUG] Setting ignored files:', ignoredFiles);
        console.log('🔍 [LOAD IGNORED DEBUG] Setting ignored chunks:', ignoredChunks);
        console.log('🔍 [LOAD IGNORED DEBUG] All ignored_chunks_status entries:', response.data.ignored_chunks_status);
        
        setIgnoredFiles(prev => {
          const updated = { ...prev, ...ignoredFiles };
          console.log('🔍 [LOAD IGNORED DEBUG] Updated ignoredFiles state:', updated);
          return updated;
        });
        setIgnoredChunks(prev => ({ ...prev, ...ignoredChunks }));
      }
    } catch (error) {
      console.error('Error loading ignored state:', error);
    }
  };

  const toggleDocument = (filename) => {
    setExpandedDocs(prev => ({
      ...prev,
      [filename]: !prev[filename]
    }));
  };

  const processChunk = async (file, chunk, fileData) => {
    const chunkKey = `${file.filename}_${chunk.chunk_index}`;
    
    // Check if this chunk is already processed (for re-process scenario)
    const isReprocess = processedChunks[chunkKey] === true;
    if (isReprocess) {
      console.log(`🔄 [RE-PROCESS] Re-processing already completed chunk: ${chunkKey}`);
    }
    
    try {
      // Guard against empty payloads (e.g., missing converted PDF attachment)
      if (!file.fileData) {
        console.warn('❌ processChunk aborted: missing fileData for', {
          filename: file.filename,
          chunkIndex: chunk.chunk_index
        });
        alert(`Cannot process chunk: missing file data for ${file.filename}. Please re-upload or retry.`);
        return;
      }
      setProcessingChunks(prev => ({ ...prev, [chunkKey]: true }));
      setGlobalProgress(0); // Reset progress for chunk processing
      
      // Create FormData for the chunk processing request
      const formData = new FormData();
      // Ensure we append a real File instance
      const fileToSend = (file.fileData instanceof File)
        ? file.fileData
        : (() => {
            try {
              const name = file.fileData?.name || file.filename || 'document.pdf';
              const type = file.fileData?.type || 'application/pdf';
              return new File([file.fileData], name, { type });
            } catch (e) {
              return file.fileData;
            }
          })();
      formData.append('file', fileToSend);
      formData.append('loan_id', loanId);
      formData.append('file_type', file.file_type || 'additional');
      formData.append('chunk_index', chunk.chunk_index);
      formData.append('start_page', chunk.start_page);
      formData.append('end_page', chunk.end_page);
      formData.append('force_reprocess', isReprocess); // Force re-run OCR if already processed
      
      // Wait for WebSocket connection to be ready BEFORE getting socket_id
      // This ensures we have a valid socket_id after any reconnection that may have occurred
      try {
        await socketService.waitForConnection();
        console.log('✅ [ChunkingModal] WebSocket connection ready');
      } catch (error) {
        console.warn('⚠️ [ChunkingModal] WebSocket connection timeout:', error);
      }
      
      // Get socket_id AFTER connection is established to ensure it's valid
      let sid = socketService.getSocketId();
      console.log('🔌 [ChunkingModal] Socket ID after connection:', sid);
      
      // Validate socket_id - retry if null (socket might still be connecting)
      if (!sid) {
        console.warn('⚠️ [ChunkingModal] Socket ID is null, waiting and retrying...');
        // Wait a bit more and retry
        await new Promise(resolve => setTimeout(resolve, 500));
        sid = socketService.getSocketId();
        console.log('🔌 [ChunkingModal] Socket ID after retry:', sid);
      }
      
      // Include socket_id so backend can target this client for progress updates
      if (sid) {
        formData.append('socket_id', sid);
        console.log('✅ [ChunkingModal] Socket ID added to request:', sid);
      } else {
        console.warn('⚠️ [ChunkingModal] No socket ID available - progress updates may not work');
      }

      // Debug: log FormData fields (names only, avoid logging huge blobs)
      try {
        const fdSummary = [];
        for (const [k, v] of formData.entries()) {
          if (k === 'file' && v && typeof v === 'object') {
            fdSummary.push({ key: k, name: v.name, size: v.size, type: v.type });
          } else {
            fdSummary.push({ key: k, value: String(v) });
          }
        }
        console.log('📦 [CHUNK FORM DATA] process-chunk payload summary:', fdSummary);
      } catch (e) {
        console.warn('⚠️ Could not inspect FormData for process-chunk');
      }
      
      console.log(`📄 Processing chunk ${chunk.chunk_index} for ${file.filename}...`);
      
      const response = await api.post('/chunks/process-chunk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000 // 5 minute timeout for Textract
      });
      
      if (response.data.success) {
        console.log(`✅ Chunk ${chunk.chunk_index} processed successfully`);
        setProcessedChunks(prev => ({ ...prev, [chunkKey]: true }));
        
        // Update chunk data with the new database ID from the API response
        if (response.data.file_id) {
          setChunkData(prev => ({
            ...prev,
            [chunkKey]: {
              ...prev[chunkKey],
              id: response.data.file_id
            }
          }));
          // console.log(`🔍 [CHUNK DEBUG] Updated chunk data with file_id:`, {
          //   chunkKey,
          //   fileId: response.data.file_id
          // });
        }
        
        // Reload chunk status to ensure all chunk data is up to date
        console.log('🔄 Reloading chunk status after processing...');
        await checkProcessedChunks();
        
        // Debug: Log current processed chunks state
        // console.log('🔍 [CHUNK DEBUG] After processing, current processed chunks:', Object.keys(processedChunks));
        // console.log('🔍 [CHUNK DEBUG] Current chunk data keys:', Object.keys(chunkData));
      }
    } catch (error) {
        console.error(`❌ Error processing chunk ${chunk.chunk_index}:`, error);
        
        // Extract error message, handling both string and object errors
        let errorMessage = 'Unknown error';
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } else if (error.response?.data?.error) {
          const errorData = error.response.data.error;
          errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error === 'object') {
          errorMessage = JSON.stringify(error);
        }
        
        alert(`Failed to process chunk ${chunk.chunk_index}: ${errorMessage}`);
    } finally {
      setProcessingChunks(prev => ({ ...prev, [chunkKey]: false }));
      
      // Clear progress state when chunk processing completes
      setProgressMessage('');
      setChunkProgress({});
    }
  };

  const processRegularFile = async (file) => {
    const identifier = file.filename; // Use filename as identifier for regular files
    
    // Check if this file is already processed (for re-process scenario)
    const isReprocess = processedRegularFiles[identifier] === true;
    if (isReprocess) {
      console.log(`🔄 [RE-PROCESS] Re-processing already completed file: ${identifier}`);
    }
    
    setIsProcessingRegularFile(prev => ({ ...prev, [identifier]: true }));
    setProgressMessage(`${isReprocess ? 'Re-processing' : 'Processing'} ${file.filename}...`);
    setChunkProgress({}); // Clear previous progress
    setGlobalProgress(0); // Reset global progress to 0

    try {
      // Process regular file with upload + OCR (no extraction)
      const formData = new FormData();
      
      // For reprocessing: Don't send file - backend will fetch from S3
      // For regular processing: Require file data
      if (!isReprocess) {
        // Check if fileData exists for regular processing
        if (!file.fileData) {
          console.error(`❌ Cannot process file ${file.filename}: fileData is missing`);
          dispatch(addNotification({
            type: 'error',
            message: `❌ Cannot process ${file.filename}: File data is not available. Please re-upload the file.`,
            duration: 5000
          }));
          return;
        }
        
        // DEBUG: Check what we're sending
        console.log('🔍 [PROCESS DEBUG] File object being sent:', {
          filename: file.filename,
          fileData: file.fileData,
          fileDataName: file.fileData?.name,
          fileDataSize: file.fileData?.size,
          fileDataType: file.fileData?.type,
          hasPreviewUrl: !!file.fileData?.previewUrl,
          fileDataKeys: file.fileData ? Object.keys(file.fileData) : 'no fileData'
        });
        
        // Ensure we have a valid File object
        let fileToSend = file.fileData;
        if (!(fileToSend instanceof File)) {
          // Try to convert to File if it's not already
          try {
            const name = file.fileData?.name || file.filename || 'document.pdf';
            const type = file.fileData?.type || 'application/pdf';
            fileToSend = new File([file.fileData], name, { type });
          } catch (e) {
            console.error('❌ Failed to create File object:', e);
            dispatch(addNotification({
              type: 'error',
              message: `❌ Cannot process ${file.filename}: Invalid file data format.`,
              duration: 5000
            }));
            return;
          }
        }
        
        // The fileData should already be the optimized file from the matching process
        formData.append('file', fileToSend); // Pass the optimized File object
      } else {
        // Reprocessing: Don't send file, backend will fetch from S3
        console.log(`🔄 [RE-PROCESS] Reprocessing ${file.filename} - backend will fetch from S3`);
      }
      formData.append('loan_id', loanId);
      formData.append('filename', file.filename);
      // Determine document type from filename
      const documentType = determineDocumentType(file.filename);
      formData.append('file_type', documentType);
      formData.append('force_reprocess', isReprocess); // Force re-run OCR if already processed
      
      // Wait for WebSocket connection to be ready BEFORE getting socket_id
      // This ensures we have a valid socket_id after any reconnection that may have occurred
      try {
        await socketService.waitForConnection();
        console.log('✅ [ChunkingModal] WebSocket connection ready for regular file');
      } catch (error) {
        console.warn('⚠️ [ChunkingModal] WebSocket connection timeout:', error);
      }
      
      // Get socket_id AFTER connection is established to ensure it's valid
      let socketId = socketService.getSocketId();
      console.log('🔌 [ChunkingModal] Socket ID after connection (regular file):', socketId);
      
      // Validate socket_id - retry if null (socket might still be connecting)
      if (!socketId) {
        console.warn('⚠️ [ChunkingModal] Socket ID is null, waiting and retrying...');
        // Wait a bit more and retry
        await new Promise(resolve => setTimeout(resolve, 500));
        socketId = socketService.getSocketId();
        console.log('🔌 [ChunkingModal] Socket ID after retry (regular file):', socketId);
      }
      
      // Include socket_id so backend can target this client for progress updates
      if (socketId) {
        formData.append('socket_id', socketId);
        console.log('✅ [ChunkingModal] Socket ID added to request (regular file):', socketId);
      } else {
        console.warn('⚠️ [ChunkingModal] No socket ID available - progress updates may not work');
      }
      
      const response = await api.post('/chunks/process-regular-file', formData, {
        onDownloadProgress: (progressEvent) => {
          // Textract progress is handled via WebSocket
        }
      });

      if (response.data.success) {
        console.log(`✅ [PROCESS SUCCESS] File ${file.filename} processed successfully!`);
        console.log(`🔍 [PROCESS SUCCESS] Updating processedRegularFiles for: ${identifier}`);
        
        dispatch(addNotification({
          type: 'success',
          message: `✅ File ${file.filename} processed successfully!`,
          duration: 3000
        }));
        
        setProcessedRegularFiles(prev => {
          const newState = { ...prev, [identifier]: true };
          console.log(`🔍 [PROCESS SUCCESS] New processedRegularFiles state:`, newState);
          return newState;
        });
        
        // Update progress bar to 100% for this file
        setChunkProgress(prev => ({ ...prev, [identifier]: 100 }));
        
        console.log(`✅ [PROCESS SUCCESS] File ${file.filename} marked as processed`);
      } else {
        throw new Error(response.data.detail || 'Unknown error processing file');
      }
    } catch (error) {
      console.error(`❌ Error processing file ${file.filename}:`, error);
      
      // Extract error message, handling both string and object errors
      let errorMessage = 'Unknown error';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        errorMessage = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        errorMessage = JSON.stringify(error);
      }
      
      dispatch(addNotification({
        type: 'error',
        message: `❌ Error processing file ${file.filename}: ${errorMessage}`,
        duration: 5000
      }));
    } finally {
      setIsProcessingRegularFile(prev => ({ ...prev, [identifier]: false }));
      setProgressMessage(''); // Clear progress message
      setChunkProgress({}); // Clear progress
      checkProcessedChunks(); // Re-check all items to update "Run Match Process" button
    }
  };

  const getTotalChunks = () => {
    return files.reduce((total, file) => {
      // Handle regular files (no chunks) - only count if not ignored
      if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
        if (isFileIgnored(file)) {
          return total; // Skip ignored regular files
        }
        return total + 1; // Each regular file counts as one item to process
      }
      // Handle chunked files - count all chunks (ignore status doesn't affect processing count)
      if (file.chunks && Array.isArray(file.chunks)) {
        return total + file.chunks.length;
      }
      return total;
    }, 0);
  };

  const getProcessedCount = () => {
    let processedCount = 0;
    
    // Count processed chunks (only non-ignored ones)
    const processedChunkKeys = Object.keys(processedChunks).filter(key => {
      if (!processedChunks[key]) return false;
      
      // Check if this chunk is ignored (file-level ignore doesn't apply to chunks)
      // The key format is: filename_chunkIndex, but filename might contain underscores
      // So we need to find the last underscore to split properly
      const lastUnderscoreIndex = key.lastIndexOf('_');
      const filename = key.substring(0, lastUnderscoreIndex);
      const chunkIndex = key.substring(lastUnderscoreIndex + 1);
      
      const file = files.find(f => (f.filename || f.name) === filename);
      if (!file) {
        console.log('🔍 File not found for chunk key:', key, 'filename:', filename, 'available files:', files.map(f => f.filename || f.name));
        return false;
      }
      
      const chunk = file.chunks?.find(c => c.chunk_index === parseInt(chunkIndex));
      return chunk; // Count all processed chunks, regardless of ignore status
    });
    
    processedCount += processedChunkKeys.length;
    
    // Count processed regular files (only non-ignored ones)
    const processedRegularKeys = Object.keys(processedRegularFiles).filter(key => {
      if (!processedRegularFiles[key]) return false;
      
      // Check if this file is ignored
      const file = files.find(f => (f.filename || f.name) === key);
      return file && !isFileIgnored(file);
    });
    
    processedCount += processedRegularKeys.length;
    
    console.log('🔍 Progress Debug:', {
      processedChunks: Object.keys(processedChunks),
      processedRegularFiles: Object.keys(processedRegularFiles),
      processedChunkKeys,
      processedRegularKeys,
      totalProcessed: processedCount
    });
    
    return processedCount;
  };

  const getNonIgnoredChunks = () => {
    return files.reduce((total, file) => {
      // Handle regular files (no chunks) - only count if not ignored
      if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
        if (isFileIgnored(file)) {
          return total; // Skip ignored regular files
        }
        return total + 1; // Each regular file counts as one item to process
      }
      // Handle chunked files - count only non-ignored chunks
      if (file.chunks && Array.isArray(file.chunks)) {
        const nonIgnoredChunks = file.chunks.filter(chunk => !isChunkIgnored(file, chunk));
        return total + nonIgnoredChunks.length;
      }
      return total;
    }, 0);
  };

  const allChunksProcessed = () => {
    // Check if all documents are complete (all chunks either processed or ignored)
    return files.every(file => getDocumentStatus(file) === 'complete');
  };

  const getUnmutedDocumentsCount = () => {
    return files.filter(file => {
      // For regular files, check if file is ignored
      if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
        return !isFileIgnored(file);
      }
      // For chunked files, check if any chunks are not ignored
      return file.chunks && file.chunks.some(chunk => !isChunkIgnored(file, chunk));
    }).length;
  };

  const getProcessedDocumentsCount = () => {
    return files.filter(file => {
      // For regular files, check if processed
      if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
        const filename = file.filename || file.name;
        return processedRegularFiles[filename];
      }
      // For chunked files, check if all non-ignored chunks are processed
      if (file.chunks) {
        const nonIgnoredChunks = file.chunks.filter(chunk => !isChunkIgnored(file, chunk));
        return nonIgnoredChunks.every(chunk => {
          const chunkKey = `${file.filename}_${chunk.chunk_index}`;
          return processedChunks[chunkKey];
        });
      }
      return false;
    }).length;
  };

  const isProcessing = () => {
    return Object.values(processingChunks).some(p => p);
  };


  const toggleChunkIgnored = async (file, chunk) => {
    const chunkKey = `${file.filename}_${chunk.chunk_index}`;
    const newIgnoredState = !ignoredChunks[chunkKey];
    
    try {
      // Get the file ID from stored chunk data
      const storedChunk = chunkData[chunkKey];
      const fileId = storedChunk?.id;
      
      console.log('🔍 [MUTE DEBUG] Toggle chunk ignored:', {
        chunkKey,
        file: file.filename,
        chunk: chunk.chunk_index,
        storedChunk,
        fileId,
        allChunkDataKeys: Object.keys(chunkData)
      });
      
      if (fileId) {
        // Make API call to persist the change
        await api.put(`/database/toggle-ignored/${loanId}/${fileId}?ignored=${newIgnoredState}`);
        
        // Update frontend state only after successful API call
        setIgnoredChunks(prev => ({
          ...prev,
          [chunkKey]: newIgnoredState
        }));
        
        console.log(`✅ Chunk ${chunk.chunk_index + 1} ${newIgnoredState ? 'muted' : 'unmuted'} successfully`);
      } else {
        console.error('❌ No file ID found for chunk:', chunkKey, 'stored data:', storedChunk);
      }
    } catch (error) {
      console.error('❌ Error toggling chunk ignored state:', error);
      // Don't update frontend state if API call failed
    }
  };

  const isChunkIgnored = (file, chunk) => {
    const chunkKey = `${file.filename}_${chunk.chunk_index}`;
    
    // Check if this chunk is processed first
    const isProcessed = processedChunks[chunkKey];
    
    let isIgnored = false;
    if (isProcessed) {
      // For processed chunks, check database ignored state
      isIgnored = ignoredChunks[chunkKey] || false;
      // console.log(`🔍 [IS CHUNK IGNORED DEBUG] Processed chunk: ${chunkKey}, ignored: ${isIgnored}`);
    } else {
      // For unprocessed chunks, only check local state
      isIgnored = localIgnoredChunks[chunkKey] || false;
      // console.log(`🔍 [IS CHUNK IGNORED DEBUG] Unprocessed chunk: ${chunkKey}, ignored: ${isIgnored}`);
    }
    
    // console.log(`🔍 [IS CHUNK IGNORED DEBUG] Available ignoredChunks keys:`, Object.keys(ignoredChunks));
    // console.log(`🔍 [IS CHUNK IGNORED DEBUG] Available localIgnoredChunks keys:`, Object.keys(localIgnoredChunks));
    // console.log(`🔍 [IS CHUNK IGNORED DEBUG] ignoredChunks[${chunkKey}]:`, ignoredChunks[chunkKey]);
    // console.log(`🔍 [IS CHUNK IGNORED DEBUG] localIgnoredChunks[${chunkKey}]:`, localIgnoredChunks[chunkKey]);
    
    return isIgnored;
  };

  const toggleFileIgnored = async (file) => {
    const filename = file.filename || file.name;
    const newIgnoredState = !ignoredFiles[filename];
    
    console.log(`🔍 [IGNORE DEBUG] Clicked ignore for:`, {
      filename,
      newIgnoredState,
      isProcessed: processedRegularFiles[filename],
      hasFileData: !!file.fileData,
      fileHash: file.fileData?.file_hash,
      fileData: file.fileData
    });
    
    // Let's see the full file object structure
    console.log(`🔍 [IGNORE DEBUG] Full file object:`, file);
    console.log(`🔍 [IGNORE DEBUG] fileData keys:`, file.fileData ? Object.keys(file.fileData) : 'no fileData');
    
    try {
      // Check if this file is processed (has file_hash)
      const isProcessed = processedRegularFiles[filename];
      
      if (isProcessed && file.file_id) {
        // File is processed and has a file_id - update database
        try {
          console.log(`🔍 [IGNORE DEBUG] Making API call to update database...`);
          console.log(`🔍 [IGNORE DEBUG] Using file_id: ${file.file_id}`);
          
          // Use file_id to update ignored state in database
          const apiEndpoint = `/database/toggle-ignored/${loanId}/${file.file_id}?ignored=${newIgnoredState}`;
          
          console.log(`🔍 [IGNORE DEBUG] API endpoint: ${apiEndpoint}`);
          await api.put(apiEndpoint);
          
          console.log(`✅ File ${filename} ${newIgnoredState ? 'ignored' : 'unignored'} in database (file_id: ${file.file_id})`);
        } catch (apiError) {
          console.error('❌ Error updating ignored state in database:', apiError);
          console.log(`⚠️ Falling back to local state only for ${filename}`);
        }
      } else {
        console.log(`🔍 [IGNORE DEBUG] File ${filename} not processed or no file_id - updating local state only`);
        console.log(`🔍 [IGNORE DEBUG] isProcessed: ${isProcessed}, hasFileId: ${!!file.file_id}`);
      }
      
      // Update local state regardless
      setIgnoredFiles(prev => ({
        ...prev,
        [filename]: newIgnoredState
      }));
      
      console.log(`✅ File ${filename} ${newIgnoredState ? 'ignored' : 'unignored'} (local state updated)`);
    } catch (error) {
      console.error('❌ Error toggling file ignored state:', error);
    }
  };

  const isFileIgnored = (file) => {
    const filename = file.filename || file.name;
    return ignoredFiles[filename] || false;
  };

  const toggleLocalChunkIgnored = (file, chunk) => {
    const chunkKey = `${file.filename}_${chunk.chunk_index}`;
    const newIgnoredState = !localIgnoredChunks[chunkKey];
    setLocalIgnoredChunks(prev => ({
      ...prev,
      [chunkKey]: newIgnoredState
    }));
    console.log(`✅ Chunk ${chunk.chunk_index + 1} ${newIgnoredState ? 'locally ignored' : 'unignored'}`);
  };

  return (
    <ModalPortal isOpen={true}>
      <ModalOverlay>
        <ModalContent>
        <ModalHeader>
          <Title>📄 Process Documents</Title>
          <CloseButton onClick={onCancel}>&times;</CloseButton>
        </ModalHeader>

        {/* Initial Processing Spinner */}
        {isInitializing && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <div style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Processing files...
            </div>
            <div style={{
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Loading document information and checking status
            </div>
          </div>
        )}

        {/* Progress Bar - Completely isolated from modal */}
        {progressMessage && (
          <div style={{
            position: 'fixed',
            top: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001, // Higher than modal
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '16px 24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid #e0e0e0',
            width: 'calc(90vw - 48px)',
            maxWidth: '952px',
            pointerEvents: 'auto', // Allow interaction with close button
            isolation: 'isolate' // Create new stacking context
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              position: 'relative'
            }}>
              {/* Close button - only show at 100% */}
              {globalProgress === 100 && (
                <button
                  onClick={() => {
                    setProgressMessage('');
                    setGlobalProgress(0);
                  }}
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-8px',
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#64748b',
                    lineHeight: '1',
                    padding: '0',
                    transition: 'all 0.2s',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#334155';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#64748b';
                  }}
                  title="Close"
                >
                  ×
                </button>
              )}
              
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#1976d2'
              }}>
                {progressMessage}
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease',
                  width: `${globalProgress}%`
                }} />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                color: '#64748b'
              }}>
                <span>Processing...</span>
                <span>{globalProgress}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Always reserve space to prevent layout shifts */}
        <div style={{ 
          opacity: isInitializing ? 0 : 1, 
          transition: 'opacity 0.3s ease',
          minHeight: isInitializing ? '200px' : 'auto' /* Reserve space during initialization */
        }}>
          <Description>
            Process your documents by clicking "Process" for each file.
            For large documents, they will be split into chunks that you can process individually.
            You can Mute any file or chunk to exclude it from the matching process.
          </Description>

        {/* UTIF Test - Simple test with file input */}

        <ProgressText>
          Progress: {getProcessedDocumentsCount()} / {getUnmutedDocumentsCount()} documents processed ({files.length - getUnmutedDocumentsCount()} ignored)
        </ProgressText>

        {files.length === 0 ? (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <p style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '500' }}>
              No documents found
            </p>
            <p style={{ margin: 0 }}>
              Go back and add some documents to get started.
            </p>
          </div>
        ) : (
          files.map((file, index) => {
          const filename = file.filename || file.name || file.fileData?.name || `File ${index + 1}`;
          const pageCount = file.page_count === 'Regular' ? 1 : (file.page_count || (file.fileData?.size ? Math.floor(file.fileData.size / 50000) : 'Unknown'));
          const chunkCount = file.chunks ? file.chunks.length : 0;
          const isRegularFile = !file.chunks || file.chunks.length === 0;
          
          
          return (
          <DocumentSection 
            key={filename || `file-${index}`}
            style={{
              opacity: isFileIgnored(file) ? 0.5 : 1,
              backgroundColor: isFileIgnored(file) ? '#f3f4f6' : 'transparent'
            }}
          >
            {isRegularFile ? (
              <DocumentHeader>
                <DocumentTitle>
                  {filename}
                  <PageCount>({pageCount} pages)</PageCount>
                  {file.fileData?.previewUrl && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '11px', 
                      color: '#3b82f6',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      TIF→JPG
                      <Search 
                        size={12} 
                        style={{ 
                          cursor: 'pointer',
                          color: '#3b82f6'
                        }}
                        onClick={() => {
                          console.log('🔍 Preview button clicked for:', filename);
                          console.log('🔍 Preview URL:', file.fileData.previewUrl);
                          
                          // Show file size information in the preview
                          const originalSize = file.fileData.originalSize ? (file.fileData.originalSize / 1024 / 1024).toFixed(2) : 'Unknown';
                          const currentSize = file.fileData.size ? (file.fileData.size / 1024 / 1024).toFixed(2) : 'Unknown';
                          
                          console.log(`📊 File size comparison: ${originalSize}MB → ${currentSize}MB`);
                          
                          window.open(file.fileData.previewUrl, '_blank');
                        }}
                        title={`Preview optimized file (${file.fileData.originalSize ? (file.fileData.originalSize / 1024 / 1024).toFixed(2) : 'Unknown'}MB → ${file.fileData.size ? (file.fileData.size / 1024 / 1024).toFixed(2) : 'Unknown'}MB)`}
                      />
                    </span>
                  )}
                  {/* Debug: Show file_hash and file_id */}
                  {file.file_hash && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '10px', 
                      color: '#666',
                      fontFamily: 'monospace'
                    }}>
                      Hash: {file.file_hash.substring(0, 8)}...
                    </span>
                  )}
                  {file.file_id && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '10px', 
                      color: '#666',
                      fontFamily: 'monospace'
                    }}>
                      ID: {file.file_id}
                    </span>
                  )}
                </DocumentTitle>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <StatusIndicator>
                    <StatusIcon 
                      $status={getDocumentStatus(file)}
                      title={getDocumentStatus(file) === 'complete' ? 'All files addressed' : 'Process or ignore remaining files'}
                    >
                      {getDocumentStatus(file) === 'complete' ? <CheckCircle /> : <AlertCircle />}
                    </StatusIcon>
                  </StatusIndicator>
                  {processedRegularFiles[filename] ? (
                    <Checkmark
                      onClick={() => processRegularFile(file)}
                      disabled={!!isProcessing()}
                      title="Click to re-process this file (will re-run OCR)"
                      onMouseEnter={(e) => {
                        const textNode = e.currentTarget.childNodes[1];
                        if (textNode) textNode.textContent = 'Click to re-process';
                      }}
                      onMouseLeave={(e) => {
                        const textNode = e.currentTarget.childNodes[1];
                        if (textNode) textNode.textContent = 'Complete';
                      }}
                    >
                      <CircleCheck size={20} />
                      Complete
                    </Checkmark>
                  ) : (
                    <ProcessButton
                      onClick={() => processRegularFile(file)}
                      disabled={!!(isProcessing() || isProcessingRegularFile[filename])}
                    >
                      {isProcessingRegularFile[filename] ? 'Processing...' : 'Process'}
                    </ProcessButton>
                  )}
                  <button
                    onClick={() => toggleFileIgnored(file)}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {isFileIgnored(file) ? 'Unignore' : 'Ignore'}
                  </button>
                </div>
              </DocumentHeader>
            ) : (
              <DocumentHeader onClick={() => toggleDocument(filename)}>
                <DocumentTitle>
                  {filename}
                  <PageCount>({pageCount} pages, {chunkCount} chunks)</PageCount>
                </DocumentTitle>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <StatusIndicator>
                    <StatusIcon 
                      $status={getDocumentStatus(file)}
                      title={getDocumentStatus(file) === 'complete' ? 'All files addressed' : 'Process or ignore remaining files'}
                    >
                      {getDocumentStatus(file) === 'complete' ? <CheckCircle /> : <AlertCircle />}
                    </StatusIcon>
                  </StatusIndicator>
                  <ChevronDown 
                    size={16} 
                    style={{ 
                      color: '#666', 
                      transition: 'transform 0.2s ease',
                      transform: expandedDocs[filename] ? 'rotate(0deg)' : 'rotate(-90deg)'
                    }} 
                  />
                </div>
              </DocumentHeader>
            )}
            
            {!isRegularFile && (
              <ChunkList $expanded={expandedDocs[filename]}>
                {file.chunks && file.chunks.length > 0 ? file.chunks.map((chunk) => {
                const chunkKey = `${filename}_${chunk.chunk_index}`;
                const isProcessed = processedChunks[chunkKey];
                const isProcessingNow = processingChunks[chunkKey];
                const isIgnored = isChunkIgnored(file, chunk);
                
                return (
                  <ChunkItem key={chunkKey}>
                    <ChunkInfo>
                      <ChunkLabel>
                        Chunk {chunk.chunk_index + 1}: {chunk.label}
                        {isIgnored && (
                          <span style={{ fontSize: '12px', color: '#d97706', fontStyle: 'italic', marginLeft: '8px' }}>
                            (Excluded from Match Process)
                          </span>
                        )}
                      </ChunkLabel>
                    </ChunkInfo>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MuteButton
                        ignored={isIgnored}
                        onClick={() => {
                          if (isProcessed) {
                            // For processed chunks, use database toggle
                            toggleChunkIgnored(file, chunk);
                          } else {
                            // For unprocessed chunks, use local toggle
                            toggleLocalChunkIgnored(file, chunk);
                          }
                        }}
                        disabled={!!isProcessingNow}
                      >
                        {isIgnored ? <VolumeX size={14} /> : <Volume2 size={14} />}
                        {isIgnored ? 'Unmute' : 'Mute'}
                      </MuteButton>
                      
                      {isProcessed ? (
                        <Checkmark
                          onClick={() => processChunk(file, chunk, file.fileData)}
                          disabled={!!isProcessing()}
                          title="Click to re-process this chunk (will re-run OCR)"
                          onMouseEnter={(e) => {
                            const textNode = e.currentTarget.childNodes[1];
                            if (textNode) textNode.textContent = 'Click to re-process';
                          }}
                          onMouseLeave={(e) => {
                            const textNode = e.currentTarget.childNodes[1];
                            if (textNode) textNode.textContent = 'Complete';
                          }}
                        >
                          <CircleCheck size={20} />
                          Complete
                        </Checkmark>
                      ) : (
                        <ProcessButton
                          $processing={isProcessingNow}
                          disabled={!!isProcessing()}
                          onClick={() => processChunk(file, chunk, file.fileData)}
                        >
                          {isProcessingNow ? 'Processing...' : 'Process'}
                        </ProcessButton>
                      )}
                    </div>
                  </ChunkItem>
                );
              }) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                  No chunks available yet. Click "Process" to start chunking this file.
                </div>
              )}
            </ChunkList>
            )}
          </DocumentSection>
          );
        })
        )}

            <ButtonGroup>
              <SecondaryButton onClick={onCancel}>
                Cancel
              </SecondaryButton>
              
              <PrimaryButton 
                disabled={files.length === 0 || !allChunksProcessed() || isProcessing()}
                onClick={() => {
              // Filter out muted chunks before proceeding with extraction
              console.log('🔍 [CHUNKING MODAL] Starting filtering process...');
              console.log('🔍 [CHUNKING MODAL] Original files:', files.length);
              
              const unmutedFiles = files.map(file => {
                console.log(`🔍 [CHUNKING MODAL] Processing file: ${file.filename}, isExisting: ${file.isExisting}, file_id: ${file.file_id}`);
                
                if (file.page_count === 'Regular' || !file.chunks || file.chunks.length === 0) {
                  // For regular files, only include if not ignored
                  const isIgnored = isFileIgnored(file);
                  console.log(`🔍 [CHUNKING MODAL] Regular file ${file.filename}: ignored=${isIgnored}, isExisting=${file.isExisting}`);
                  return isIgnored ? null : file;
                } else {
                  // For chunked files, filter out ignored chunks
                  console.log(`🔍 [CHUNKING MODAL] Chunked file ${file.filename}: ${file.chunks.length} chunks`);
                  
                  const unmutedChunks = file.chunks.filter(chunk => {
                    const isIgnored = isChunkIgnored(file, chunk);
                    console.log(`🔍 [CHUNKING MODAL] Chunk ${chunk.chunk_index}: ignored=${isIgnored}`);
                    return !isIgnored;
                  });
                  
                  console.log(`🔍 [CHUNKING MODAL] File ${file.filename}: ${unmutedChunks.length}/${file.chunks.length} chunks unmuted`);
                  
                  if (unmutedChunks.length === 0) {
                    console.log(`🔍 [CHUNKING MODAL] File ${file.filename}: ALL CHUNKS MUTED - excluding from extraction`);
                    return null; // All chunks are muted, exclude this file
                  }
                  
                  return {
                    ...file,
                    chunks: unmutedChunks
                  };
                }
              }).filter(file => file !== null);
              
              console.log('🔍 [CHUNKING MODAL] Filtering for extraction:', {
                originalFiles: files.length,
                unmutedFiles: unmutedFiles.length,
                files: unmutedFiles.map(f => ({ name: f.filename, chunks: f.chunks?.length || 'regular' }))
              });
              
              // Pass the filtered files to the parent
              onComplete(unmutedFiles);
            }}
          >
                Run Match Process
              </PrimaryButton>
            </ButtonGroup>
        </div>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default ChunkingModal;

